/* Create Tracking Event Dialog Form
 * 
 * Angular module: trackerbase-app.create-tracking-event-dialog.dialog
 * Provides a controller: createTrackingEventDialogCtrl
 */

import angular from "angular";
import kendo from "@progress/kendo-ui";
import "kendo.timezones";

import ngAnimate from "angular-animate";
import ngSanitize from "angular-sanitize";
import ngAria from "angular-aria";

import uiBootstrap from "angular-ui-bootstrap";

import TimeZoneServiceModule from "../../services/TimezoneService";
import TimeZoneNameFormatFilterModule from "../../filters/FormatTimeZoneNameFilter";
import AllStatesServiceModule from "../../services/AllStatesService";
import TrackingEventFactoriesModule from "../../models/factories/tracking-event-factories";
import UserMessagesServiceModule from "../../services/UserMessagesService";
import CalendarGlobalViewServiceModule from "../../services/CalendarGlobalViewService";
import kendoDirectives from "../../shims/kendoDirectives";

import Assert from "tbAssert";

import { GlobalViewNames } from "tbClient/services/WellKnownGlobalViews";

const mTrackingEventUnderConstruction = new WeakMap();

const mScopeSvc = new WeakMap();
const mDocumentSvc = new WeakMap();
const mLogSvc = new WeakMap();
const mModalInstanceSvc = new WeakMap();
const mTimeZoneSvc = new WeakMap();
const mFormatTZNameFilterSvc = new WeakMap();
const mAllStatesSvc = new WeakMap();
const mAvailableTimeZonesDataSource = new WeakMap();

const mAvailableTimeZones = new WeakMap();
const mAvailableStates = new WeakMap();

const mUserMessagesSvc = new WeakMap();
const mGlobalViewSvc = new WeakMap();
const mInternalEventsHelpText = new WeakMap();
const mExternalEventsHelpText = new WeakMap();

const dialogDateTimePickerModelOptions = {
    updateOn: "blur"
};

const dialogStartDateTimePickerOptions = {
    format: "M/d/yyyy h:mm tt",
    timeFormat: "h:mm tt",
    interval: 15
};

const dialogEndDateTimePickerOptions = {
    format: "M/d/yyyy h:mm tt",
    timeFormat: "h:mm tt",
    interval: 15
};

const dialogTextFieldNgModelOptions = {
    updateOn: "blur default",
    debounce: {
        "blur": 0,
        "default": 500
    }
};

class CreateTrackingEventDialogController {
    constructor($scope,
                $document,
                $log,
                $uibModalInstance,
                TimeZone,
                tbFormatTZNameFilter,
                AllStatesService,
                TrackingEventFactory,
                UserMessages,
                CalendarGlobalViewService)
    {

        Assert.StrictAssert($scope != null, "Assertion Failure: Missing Scope Service Dependency");
        Assert.StrictAssert($document != null, "Assertion Failure: Missing Document Service Dependency");
        Assert.StrictAssert($log != null, "Assertion Failure: Missing Log Service Dependency");
        Assert.StrictAssert($uibModalInstance != null, "Assertion Failure: Missing Modal Instance Service Dependency");

        Assert.StrictAssert(TimeZone != null, "Assertion Failure: Missing Time Zone Data Service Dependency");
        Assert.StrictAssert(tbFormatTZNameFilter != null, "Assertion Failure: Missing Time Zone Name Filter Dependency");
        Assert.StrictAssert(AllStatesService != null, "Assertion Failure: Missing All States Service Dependency");
        Assert.StrictAssert(TrackingEventFactory != null, "Assertion Failure: Missing Tracking Event Factory Dependency");

        Assert.StrictAssert(UserMessages != null, "Assertion Failure: Missing User Messages Service Dependency");
        Assert.StrictAssert(CalendarGlobalViewService != null, "Assertion Failure: Missing Global View Service Dependency");

        mScopeSvc.set(this, $scope);
        mDocumentSvc.set(this, $document);
        mLogSvc.set(this, $log);
        mModalInstanceSvc.set(this, $uibModalInstance);
        mTimeZoneSvc.set(this, TimeZone);
        mFormatTZNameFilterSvc.set(this, tbFormatTZNameFilter);
        mAllStatesSvc.set(this, AllStatesService);

        mUserMessagesSvc.set(this, UserMessages);
        mInternalEventsHelpText.set(this, null);
        mExternalEventsHelpText.set(this, null);

        mGlobalViewSvc.set(this, CalendarGlobalViewService);

        mTrackingEventUnderConstruction.set(this, TrackingEventFactory.CreateNew());
        mAvailableTimeZones.set(this, []);

        mAvailableStates.set(this, null);
    }

    $onInit() {
        const usrMsgSvc = mUserMessagesSvc.get(this);
        mInternalEventsHelpText.set(this, usrMsgSvc.getUserMessage({msgId: 0x1019}));
        mExternalEventsHelpText.set(this, usrMsgSvc.getUserMessage({msgId: 0x1021}));

        const scopeSvc = mScopeSvc.get(this);
        mAllStatesSvc.get(this).GetAllRealStatesAsync().then(
            (realStateData) => {

                let preStateLoadFormStatus = undefined;
                if (mScopeSvc.get(this).newEventForm != null) {
                    preStateLoadFormStatus = mScopeSvc.get(this).newEventForm.$pristine;
                }

                mAvailableStates.set(this, this._prepareAvailableStates(realStateData));

                if (preStateLoadFormStatus === true) {
                    scopeSvc.newEventForm.$setPristine();
                }

            }, (errorResponse) => {
                mLogSvc.get(this).error("Error retrieving state data", errorResponse);
            });

        mAvailableTimeZonesDataSource.set(this, new kendo.data.DataSource({
            transport: {
                read: (options) => {
                    mTimeZoneSvc.get(this)
                                .getTimeZones({ countryCode: "US" })
                                .$promise
                                .then(
                                    (tzData) => {
                                        let preparedTzData = this._prepareAvailableTimeZoneData(tzData);
                                        options.success(preparedTzData);
                                    },
                                    (errorResponse) => {
                                        options.error(errorResponse);
                                    }
                                );
                }
            },
            schema: {
                model: {
                    id: "zoneId",
                    zoneId: { from: "zoneId" },
                    zoneDisplayName: {from: "zoneDisplayName" }
                }
            }
        }));

        scopeSvc.$on(
            "$destroy",
            () => {

                let eventViewRootElement = angular.element("#tbCreateEventModalContentRoot");
                kendo.destroy(eventViewRootElement);

            }
        );
    }

    _prepareAvailableTimeZoneData(allZones) {

        let preparedZones = allZones.map((tzName) => {

            let tzDisplayName = mFormatTZNameFilterSvc.get(this)(tzName);

            return {
                zoneDisplayName: tzDisplayName,
                zoneId: tzName
            };
        });

        return preparedZones;
    }

    _prepareAvailableStates(statesData) {
        return statesData.map((stateModel) => {
            return {
                stateAbbreviation: stateModel.shortName,
                stateName: stateModel.fullName
            };
        });
    }

    get eventUnderConstruction() {
        return mTrackingEventUnderConstruction.get(this);
    }

    get internalEventHelpText() {
        return mInternalEventsHelpText.get(this);
    }

    get externalEventHelpText() {
        return mExternalEventsHelpText.get(this);
    }

    get externalEventOptionVisible() {
        const currentView = mGlobalViewSvc.get(this).getCurrentView();
        return currentView.name !== GlobalViewNames.tracking;
    }

    get showExternalEventOptionWarning() {
        return (!this.externalEventOptionVisible) && this.eventUnderConstruction.isExternal;
    }

    get externalEventOptionWarningText() {
        return mUserMessagesSvc.get(this).getUserMessage({msgId: 0x1500});
    }

    // Default ng-model-options to have the text input controls update the
    // model immediately on loss of focus, but wait a half a second before
    // doing so after the contents change (for instance after typing)
    get dateTimePickerModelOptions() {
        return dialogDateTimePickerModelOptions;
    }

    get textFieldNgModelOptions() {
        return dialogTextFieldNgModelOptions;
    }

    // Start/End date picker options - only update the model when the control
    // loses focus.
    get startDateTimePickerOptions() {
        return dialogStartDateTimePickerOptions;
    }

    get endDateTimePickerOptions() {
        return dialogEndDateTimePickerOptions;
    }

    get availableTimeZonesDataSource() {
        return mAvailableTimeZonesDataSource.get(this);
    }

    get availableStates() {
        return mAvailableStates.get(this);
    }

    // Functions to close the modal and create or cancel creation of the
    // configured new tracking event.
    ok() {
        mModalInstanceSvc.get(this).close(mTrackingEventUnderConstruction.get(this));
    }

    cancel() {
        mModalInstanceSvc.get(this).dismiss("cancel");
    }

}

CreateTrackingEventDialogController.$inject = [
    "$scope",
    "$document",
    "$log",
    "$uibModalInstance",
    "TimeZone",
    "tbFormatTZNameFilter",
    "AllStatesService",
    "TrackingEventFactory",
    "UserMessages",
    "CalendarGlobalViewService"
];

CreateTrackingEventDialogController.dependencies = [
    ngAnimate,
    ngSanitize,
    ngAria,
    uiBootstrap,
    kendoDirectives,
    TimeZoneServiceModule,
    TimeZoneNameFormatFilterModule,
    AllStatesServiceModule,
    TrackingEventFactoriesModule,
    UserMessagesServiceModule,
    CalendarGlobalViewServiceModule
];

CreateTrackingEventDialogController.template = require("./CreateTrackingEventDialog.tpl.html");
CreateTrackingEventDialogController.controllerAs = "createTrackingEventDialog";

export default CreateTrackingEventDialogController;



//////////////////
// WEBPACK FOOTER
// ./src/views/create-tracking-event-dialog/CreateTrackingEventDialogController.js
// module id = x4gw
// module chunks = 1